import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Dumbbell, Search, CheckCircle2 } from "lucide-react";

/**
 * Picker dialog used when the user taps "Swap workout" on a day card.
 * Shows all of the user's own WorkoutPlans with a search filter and
 * highlights the currently-assigned one. Tapping a row calls `onSelect`
 * with the new plan id — the parent handles the mutation + toast.
 */
const DAY_LABELS = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

export default function SwapWorkoutDialog({
  open,
  onOpenChange,
  dayKey,
  workouts,
  currentPlanId,
  onSelect,
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return workouts;
    return workouts.filter(
      (w) =>
        (w.name || "").toLowerCase().includes(q) ||
        (w.goal || "").toLowerCase().includes(q) ||
        (w.difficulty || "").toLowerCase().includes(q),
    );
  }, [workouts, query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-[#0B1220] border-white/10 text-white"
        data-testid="swap-workout-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display">
            Swap {DAY_LABELS[dayKey] || ""} workout
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Pick a different plan from your library. The day&apos;s slot updates immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, goal or difficulty"
            className="pl-9 bg-white/[0.05] border-white/15 text-white placeholder:text-white/40"
            data-testid="swap-workout-search"
          />
        </div>

        <div className="max-h-[55vh] overflow-y-auto -mr-2 pr-2 space-y-2">
          {filtered.length === 0 && (
            <p className="text-sm text-white/55 py-6 text-center">
              No matching plans.
            </p>
          )}
          {filtered.map((w) => {
            const isCurrent = w.id === currentPlanId;
            return (
              <button
                type="button"
                key={w.id}
                onClick={() => onSelect(w.id)}
                data-testid={`swap-workout-option-${w.id}`}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition text-left ${
                  isCurrent
                    ? "border-sky-400/60 bg-sky-500/[0.08]"
                    : "border-white/10 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06]"
                }`}
              >
                <div className="w-9 h-9 rounded-full bg-sky-500/15 border border-sky-400/30 text-sky-300 flex items-center justify-center">
                  <Dumbbell className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{w.name}</p>
                  <p className="text-xs text-white/55 capitalize">
                    {(w.exercises || []).length} exercises
                    {w.difficulty ? ` · ${w.difficulty}` : ""}
                    {w.goal ? ` · ${w.goal.replace(/_/g, " ")}` : ""}
                  </p>
                </div>
                {isCurrent && <CheckCircle2 className="w-4 h-4 text-sky-400" />}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
